/**
 * One press of "Add to Cart" on the seat map, from the hold onwards: put the
 * seats that press held into the server cart, and undo everything the press
 * did when any step after the hold fails.
 *
 * Without the undo, a refused cart add left the seats HELD by the buyer's
 * session for the length of the hold (15 minutes plus the release grace) with
 * nothing in any cart: two $650 VIP tables went off sale to every other buyer
 * and to the box office because the cart refused a 12-seat request the hold
 * had accepted. A failure part-way through was worse: the ticket types added
 * before it kept their server cart rows, which the browser's cart never got,
 * and the order the buyer finally paid for would carry them.
 *
 * Only seats THIS press held are ever released; seats the buyer already had
 * in their cart before it are left alone.
 *
 * Pure orchestration: the requests are injected, so this is unit-tested
 * without a browser (see hold-with-retry for the same pattern).
 */

export interface SeatCartAddResult {
  ok: boolean
  error?: string
  /** The server cart the seats went into. */
  cartId?: string | null
}

export interface SeatCartAttemptDeps {
  /** POST /api/cart/items for one ticket type's seats. */
  addToCart: (ticketTypeId: string, seatIds: string[]) => Promise<SeatCartAddResult>
  /** Delete these seats' cart rows and release their holds (they reached the cart). */
  removeFromCart: (seatIds: string[]) => Promise<void>
  /** Release holds on seats that never reached the cart. */
  releaseHolds: (seatIds: string[]) => Promise<void>
}

export class SeatCartAddError extends Error {}

export interface SeatCartAttempt {
  /** Add one ticket type's held seats; throws SeatCartAddError when refused. */
  add: (ticketTypeId: string, seatIds: string[]) => Promise<string | null>
  /** Give back everything this press held. Safe to call more than once. */
  undo: () => Promise<void>
}

export function startSeatCartAttempt(heldSeatIds: string[], deps: SeatCartAttemptDeps): SeatCartAttempt {
  const held = new Set(heldSeatIds)
  const inCart = new Set<string>()
  let undone = false
  return {
    async add(ticketTypeId, seatIds) {
      const result = await deps.addToCart(ticketTypeId, seatIds)
      if (!result.ok) throw new SeatCartAddError(result.error || 'Failed to add seats to cart')
      for (const id of seatIds) inCart.add(id)
      return result.cartId ?? null
    },
    async undo() {
      if (undone) return
      undone = true
      const added = [...inCart]
      const notAdded = [...held].filter(id => !inCart.has(id))
      // Each one on its own: a failed removal must not stop the release.
      await Promise.all([
        added.length > 0 ? deps.removeFromCart(added).catch(() => {}) : null,
        notAdded.length > 0 ? deps.releaseHolds(notAdded).catch(() => {}) : null,
      ])
    },
  }
}
